import { forwardRef, type InputHTMLAttributes, type ReactNode } from "react";

// TextInput — DS §4.9 (.fc-input + affix). Canonical tokens only.
// States: default / hover (ink-faint border) / focus (sage border + 3px glow)
// / error (danger border + error glow) / disabled (bg-rail).

const BASE_INPUT = [
    "w-full box-border bg-surface border rounded-[var(--radius-sm)] px-4 py-3",
    "font-sans text-base text-primary outline-none",
    "transition-[border-color,box-shadow] duration-[var(--dur-base)] ease-[var(--ease-out)]",
    "placeholder:text-tertiary",
    "disabled:bg-muted disabled:text-tertiary disabled:cursor-not-allowed disabled:border-border",
].join(" ");

/* Field states, shared by TextInput, Textarea and Select so the three cannot
   drift apart. They must stay LITERAL strings: Tailwind scans source text, so a
   class assembled from a template literal is never generated. Declared here and
   imported by the other two, which is enough for the scanner to find them.

   The ramp escalates rather than wandering:

     rest    --border    1.32:1   barely there, which is the point
     hover   --interactive 3.07:1  the system acknowledging the cursor
     focus   --interactive 5.84:1  you are in this field

   --interactive is --cta-hover's value: the brand's INTERACTIVE green, not its
   status green, so focus is a fair use of it rather than a borrowed colour.

   Hover-to-focus is only 1.90:1 on the border alone, so the ring is what makes
   the change unmistakable — and its job is PRESENCE, not contrast. Absent-to-
   present registers instantly in a way a contrast delta does not, which is why
   32% is enough: it paints #d5dfda at 1.36:1, nowhere near a contrast threshold
   and not trying to be. It was 18% (#e7edea, 1.19:1), which read as nothing at
   all; 55% was tried and was too bold for a field at rest in a form. The ring
   stays sage while the border goes deep green, so focus is a soft halo around a
   crisp edge rather than one thick green band.

   Chasing contrast in the ring would be the wrong target anyway: sage cannot
   reach 3:1 on white at ANY opacity — 100% sage is 3.07:1, its own contrast. So
   the BORDER carries the accessible signal, at 5.84:1, and the ring is free to
   be quiet. The error ring matches at 32% for the same reason. */
export const FIELD_REST = "border-border hover:border-interactive";
export const FIELD_FOCUS =
    "focus:border-interactive focus:shadow-[0_0_0_3px_var(--interactive-ring)]";
/** Select drives the same look from an `open` flag rather than `:focus`. */
export const FIELD_OPEN = "border-interactive shadow-[0_0_0_3px_var(--interactive-ring)]";
/* The same two states for a control whose BOX is a WRAPPER rather than the
   focusable element — PhoneInput, where focus lands on an inner <input>. Spelled
   out rather than derived from the pair above by string replacement: Tailwind
   scans source text, so a class assembled at runtime is never generated at all. */
export const FIELD_FOCUS_WITHIN =
    "focus-within:border-interactive focus-within:shadow-[0_0_0_3px_var(--interactive-ring)]";
export const FIELD_ERROR_WITHIN =
    "border-danger focus-within:border-danger focus-within:shadow-[0_0_0_3px_rgba(161,75,58,0.32)]";

/** Error keeps --danger and only gains the same ring bump — 15% was invisible. */
export const FIELD_ERROR =
    "border-danger focus:border-danger focus:shadow-[0_0_0_3px_rgba(161,75,58,0.32)]";

function stateClasses(error?: boolean) {
    return error ? FIELD_ERROR : `${FIELD_REST} ${FIELD_FOCUS}`;
}

export interface TextInputProps extends InputHTMLAttributes<HTMLInputElement> {
    error?: boolean;
    leadingGlyph?: ReactNode;
    trailingText?: string;
}

/** forwardRef so a caller can focus or measure the control — the birthday step
 *  focuses it on mount, and TextField passes its own ref straight through. */
export const TextInput = forwardRef<HTMLInputElement, TextInputProps>(function TextInput(
    { error, leadingGlyph, trailingText, className = "", ...rest },
    ref,
) {
    const input = (
        <input
            ref={ref}
            aria-invalid={error || undefined}
            className={[
                BASE_INPUT,
                stateClasses(error),
                leadingGlyph ? "pl-12" : "",
                trailingText ? "pr-16" : "",
                className,
            ]
                .filter(Boolean)
                .join(" ")}
            {...rest}
        />
    );

    if (!leadingGlyph && !trailingText) return input;

    return (
        <div className="relative">
            {leadingGlyph && (
                <span className="pointer-events-none absolute left-3.5 top-1/2 flex -translate-y-1/2 items-center text-tertiary">
                    {leadingGlyph}
                </span>
            )}
            {input}
            {trailingText && (
                <span className="pointer-events-none absolute right-3.5 top-1/2 -translate-y-1/2 font-sans text-xs font-medium tracking-[0.04em] text-tertiary">
                    {trailingText}
                </span>
            )}
        </div>
    );
});
